import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SwipeProvider } from "./context/SwipeContext";
import { useGeolocation } from "./hooks/useGeolocation";
import { Toaster } from "./components/ui/toaster";
import ProtectedRoute from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import TopNav from "./components/TopNav";
import LocationRequired from "./components/LocationRequired";
import UnifiedAuth from "./pages/UnifiedAuth";
import VerifyCode from "./pages/VerifyCode";
import ForgotPassword from "./pages/ForgotPassword";
import CreateProfile from "./pages/CreateProfile";
import ProfileCompletion from "./pages/ProfileCompletion";
import SwipePage from "./pages/SwipePage";
import MatchesPage from "./pages/MatchesPage";
import MessagesPage from "./pages/MessagesPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";

/**
 * Location Gate Component
 * Ensures location permission is granted before allowing app access
 */
function LocationGate({ children }) {
  const { permission, loading, requestLocation } = useGeolocation();

  // Show loading while checking permission
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-lg">Checking location...</p>
        </div>
      </div>
    );
  }

  // Block app if location denied
  if (permission === 'denied') {
    return <LocationRequired onRetry={requestLocation} />;
  }

  // Allow access if granted
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<UnifiedAuth />} />
      <Route path="/login" element={<UnifiedAuth />} />
      <Route path="/signup" element={<UnifiedAuth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-code" element={<VerifyCode />} />
      <Route path="/create-profile" element={<CreateProfile />} />
      <Route 
        path="/complete-profile" 
        element={
          <ProtectedRoute>
            <ProfileCompletion />
          </ProtectedRoute>
        } 
      />
      
      <Route
        path="/swipe"
        element={
          <ProtectedRoute>
            <LocationGate>
              <div className="flex flex-col h-screen flex-layout-container">
                <TopNav />
                <div className="flex-1 overflow-y-auto pt-24 pb-24">
                  <SwipePage />
                </div>
                <BottomNav />
              </div>
            </LocationGate>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/matches"
        element={
          <ProtectedRoute>
            <LocationGate>
              <div className="flex flex-col h-screen flex-layout-container">
                <TopNav />
                <div className="flex-1 overflow-y-auto pt-24 pb-24">
                  <MatchesPage />
                </div>
                <BottomNav />
              </div>
            </LocationGate>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/messages/:matchId"
        element={
          <ProtectedRoute>
            <LocationGate>
              <div className="flex flex-col h-screen flex-layout-container">
                <TopNav />
                <div className="flex-1 overflow-hidden">
                  <MessagesPage />
                </div>
              </div>
            </LocationGate>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <LocationGate>
              <div className="flex flex-col h-screen flex-layout-container">
                <TopNav />
                <div className="flex-1 overflow-y-auto pt-24 pb-24">
                  <ProfilePage />
                </div>
                <BottomNav />
              </div>
            </LocationGate>
          </ProtectedRoute>
        }
      />
      
      <Route path="/admin" element={<AdminPage />} />
      
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SwipeProvider>
            <AppRouter />
            <Toaster />
          </SwipeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
